import { useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { MessageCircle, Phone } from 'lucide-react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Button } from '@/components/ui/button';
import { useDeal } from '@/hooks/useDeals';
import { useNotes } from '@/hooks/useNotes';
import { useInteractions } from '@/hooks/useInteractions';

function formatMoney(value?: number | null, currency?: string | null) {
  if (typeof value !== 'number') return '-';
  const code = currency || 'BRL';
  try {
    return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: code }).format(value);
  } catch {
    return `${value.toFixed(2)} ${code}`;
  }
}

function contactName(contact: any) {
  if (!contact) return 'Sem contato';
  return `${contact.first_name || ''} ${contact.last_name || ''}`.trim() || 'Sem contato';
}

function normalizePhone(phone?: string | null, countryCode?: string | null) {
  if (!phone) return '';
  const base = phone.replace(/\D/g, '');
  const code = (countryCode || '').replace(/\D/g, '');
  return code ? `+${code}${base}` : `+${base}`;
}

function formatDate(value?: string | null) {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  return date.toLocaleString('pt-BR');
}

export default function MobileDealDetail() {
  const { id } = useParams();
  const { deal, loading, error } = useDeal(id || '');

  const [newNote, setNewNote] = useState('');
  const [savingNote, setSavingNote] = useState(false);

  const {
    notes,
    loading: notesLoading,
    createNote,
  } = useNotes({ dealId: id || undefined });

  const {
    interactions,
    isLoading: interactionsLoading,
  } = useInteractions(id || '');

  const dealActivity = useMemo(() => {
    const noteItems = (notes || []).map((note) => ({
      id: `note-${note.id}`,
      created_at: note.created_at,
      label: 'Nota',
      detail: note.content || '[sem texto]',
    }));

    const interactionItems = (interactions || []).map((item) => ({
      id: `interaction-${item.id}`,
      created_at: item.created_at,
      label: `Interacao ${item.channel || ''}`.trim(),
      detail: item.notes || item.next_action || item.outcome || 'Registro de interacao',
    }));

    return [...noteItems, ...interactionItems].sort((a, b) => {
      return new Date(b.created_at || '').getTime() - new Date(a.created_at || '').getTime();
    });
  }, [notes, interactions]);

  const handleCreateNote = async () => {
    const content = newNote.trim();
    if (!content || !id) return;

    setSavingNote(true);
    try {
      await createNote({ content });
      setNewNote('');
    } finally {
      setSavingNote(false);
    }
  };

  if (loading) return <div className="p-3 text-sm text-muted-foreground">Carregando negocio...</div>;
  if (error || !deal) return <div className="p-3 text-sm text-destructive">Negocio nao encontrado.</div>;

  const phone = normalizePhone((deal as any).contact?.primary_phone, (deal as any).contact?.primary_phone_country_code);

  return (
    <div className="space-y-3 p-3">
      <div className="rounded-lg border bg-card p-3">
        <p className="text-base font-semibold">{deal.name || 'Negocio sem nome'}</p>
        <p className="mt-1 text-xs text-muted-foreground">Etapa: {deal.stage || 'Sem etapa'}</p>
        <p className="mt-1 text-sm font-medium">{formatMoney((deal as any).total_amount, (deal as any).currency)}</p>
      </div>

      <div className="grid grid-cols-2 gap-2">
        <a
          href={phone ? `tel:${phone}` : '#'}
          className="flex items-center justify-center gap-2 rounded-lg border bg-card px-3 py-2 text-sm"
        >
          <Phone className="h-4 w-4" /> Ligar
        </a>

        {(deal as any).contact?.id ? (
          <Link
            to={`/m/whatsapp/${(deal as any).contact.id}`}
            className="flex items-center justify-center gap-2 rounded-lg border bg-card px-3 py-2 text-sm"
          >
            <MessageCircle className="h-4 w-4" /> WhatsApp
          </Link>
        ) : (
          <div className="flex items-center justify-center rounded-lg border px-3 py-2 text-sm text-muted-foreground">
            Sem WhatsApp
          </div>
        )}
      </div>

      <Tabs defaultValue="details" className="w-full">
        <TabsList className="grid w-full grid-cols-3">
          <TabsTrigger value="details">Detalhes</TabsTrigger>
          <TabsTrigger value="notes">Notas</TabsTrigger>
          <TabsTrigger value="activity">Atividade</TabsTrigger>
        </TabsList>

        <TabsContent value="details" className="mt-3">
          <div className="rounded-lg border bg-card p-3 text-sm">
            <p><span className="font-medium">Contato:</span> {contactName((deal as any).contact)}</p>
            <p><span className="font-medium">Criado em:</span> {formatDate((deal as any).created_at)}</p>
            <p><span className="font-medium">Fonte:</span> {(deal as any).deals_source || 'N/A'}</p>
            <p><span className="font-medium">Canal:</span> {(deal as any).deals_channel || 'N/A'}</p>
          </div>

          {(deal as any).contact?.id ? (
            <Link to={`/m/contacts/${(deal as any).contact.id}`} className="mt-2 block rounded-lg border bg-card p-3 text-sm">
              Ver contato completo
            </Link>
          ) : null}
        </TabsContent>

        <TabsContent value="notes" className="mt-3 space-y-2">
          <div className="rounded-lg border bg-card p-2">
            <textarea
              value={newNote}
              onChange={(event) => setNewNote(event.target.value)}
              placeholder="Adicionar nota no negocio..."
              className="min-h-[80px] w-full resize-none rounded-md border p-2 text-sm"
            />
            <Button className="mt-2 w-full" disabled={savingNote || !newNote.trim()} onClick={handleCreateNote}>
              {savingNote ? 'Salvando...' : 'Salvar nota'}
            </Button>
          </div>

          {notesLoading ? <p className="text-sm text-muted-foreground">Carregando notas...</p> : null}
          {(notes || []).map((note) => (
            <div key={note.id} className="rounded-lg border bg-card p-3">
              <p className="whitespace-pre-wrap text-sm">{note.content}</p>
              <p className="mt-1 text-[11px] text-muted-foreground">{formatDate(note.created_at)}</p>
            </div>
          ))}
        </TabsContent>

        <TabsContent value="activity" className="mt-3">
          {interactionsLoading ? <p className="text-sm text-muted-foreground">Carregando atividade...</p> : null}

          <div className="space-y-2">
            {dealActivity.length === 0 ? <p className="text-xs text-muted-foreground">Sem atividade recente.</p> : null}
            {dealActivity.map((item) => (
              <div key={item.id} className="rounded-lg border bg-card p-3">
                <p className="text-sm font-medium">{item.label}</p>
                <p className="mt-1 text-xs text-muted-foreground">{item.detail}</p>
                <p className="mt-1 text-[11px] text-muted-foreground">{formatDate(item.created_at)}</p>
              </div>
            ))}
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
